import { Box, Stack, Typography } from "@mui/material";
import SpriteIcon from "../../core-components/assets/Icon";
import Button from "../../core-components/theme/components/Button/Button";
import { Neutrals, Primary } from "../../core-components/theme/color-palette/colors";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const FileUploadComponent = ({ accept, onFileClick, multiple, uploadButtonText, captionSxProps = {}, captionText, supportiveText, buttonProps = {}, stackProps = { alignItems: 'center', rowGap: 3 }, boxProps = {}, disabled = false }: any) => {
  const { t } = useTranslation();
  const resolvedUploadButtonText = uploadButtonText ?? t('common.fileUpload.clickHereToUpload', 'Click here to upload');
  const resolvedCaptionText = captionText ?? t('common.fileUpload.maxSizePerFile', 'Max size per file: 100MB');
  const resolvedSupportiveText = supportiveText ?? t('common.fileUpload.supportedTypes', 'Supported types: PDF, XLSX, XLS, CSV, DOC, DOCX, PPT, PPTX, TXT, Images, Audio & Video');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const fakeEvent = { target: { files } } as React.ChangeEvent<HTMLInputElement>;
      onFileClick(fakeEvent);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFileClick(event);
  };

  return (
    <Box
      sx={{
        border: `2px dashed ${disabled ? Neutrals[300] : (isDragging ? Primary[400] : Primary[200])}`,
        borderRadius: '6px',
        padding: '32px',
        textAlign: 'center',
        backgroundColor: disabled ? 'transparent' : (isDragging ? Primary[50] : 'transparent'),
        transition: 'all 0.2s ease-in-out',
        cursor: disabled ? 'default' : 'pointer',
        boxSizing: 'border-box',
        opacity: disabled ? 0.5 : 1
      }}
      onDrop={disabled ? undefined : handleDrop}
      onDragOver={disabled ? undefined : handleDragOver}
      onDragLeave={disabled ? undefined : handleDragLeave}
      {...boxProps}>
      <Stack {...stackProps}>
        <Typography variant="body2">{t('common.fileUpload.dragFilesHereOr', 'Drag Files here or')} <strong>{t('common.fileUpload.browse', 'Browse')}</strong></Typography>
        <SpriteIcon id={'upload_file'} width="32" height="32" />
        <>
          <input
            type="file"
            accept={accept}
            multiple={multiple}
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            disabled={disabled}
          />
          <Button variant="outlined" onClick={handleClick} {...buttonProps} disabled={disabled}>{resolvedUploadButtonText}</Button>
        </>
        <Stack>
          <Typography variant="caption" sx={{ color: Neutrals[300], fontSize: '12px', ...captionSxProps }}>{resolvedCaptionText}</Typography>
          <Typography variant="caption" sx={{ color: Neutrals[300], fontSize: '12px', ...captionSxProps }}>{resolvedSupportiveText}</Typography>
        </Stack>
      </Stack>
    </Box>
  )
}

export default FileUploadComponent;